import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildAppLink } from "../../utils/landingRedirect";
import "./YouWoConsumerWorkflowDemo.css";

type FeatureKey = "record" | "import" | "agent" | "create" | "study";

type DemoFeature = {
  key: FeatureKey;
  icon: string;
  label: string;
  title: string;
  body: string;
  sources: string[];
  steps: Array<{ title: string; body: string }>;
  outputs: string[];
};

type Props = {
  id?: string;
};

const featureOrder: FeatureKey[] = ["record", "import", "agent", "create", "study"];

const featureIcons: Record<FeatureKey, string> = {
  record: "lucide:audio-lines",
  import: "lucide:upload-cloud",
  agent: "lucide:bot",
  create: "lucide:wand-sparkles",
  study: "lucide:graduation-cap",
};

const fallback = {
  title: "Pick a workflow. YouWo prepares the next step.",
  featureLabel: "Choose a workflow",
  hoverHint: "Hover or tap",
  inputLabel: "Sources",
  activeLabel: "Working on",
  approvalLabel: "Next step",
  outputLabel: "Ready output",
  primaryCta: "Try it with your content",
  approveCta: "Approve next step",
  workspaceTitle: "YouWo Workspace",
  features: {
    record: {
      label: "Record",
      title: "Record a class or meeting and keep thinking.",
      kicker: "Live transcript, translation, and questions while you listen.",
      body: "YouWo captures the conversation, keeps timestamps, and turns the live transcript into source-backed notes.",
      prompt: "Record this lecture, translate key ideas, and mark anything that could appear on the exam.",
      sources: ["Live audio", "Transcript", "Class language"],
      steps: [
        { title: "Start recording", body: "Tap once and let the transcript stream while you stay present." },
        { title: "Capture meaning", body: "Important moments, translations, and timestamps stay connected." },
        { title: "Ask in context", body: "Ask for definitions, examples, or follow-up notes before the recording ends." },
      ],
      outputs: ["Clean transcript", "Translated notes", "Source-linked summary"],
    },
    import: {
      label: "Import",
      title: "Drop in anything you already have.",
      kicker: "PDFs, YouTube, audio, images, docs, and links become usable context.",
      body: "Imports become structured notes with sections, citations, and searchable context for chat, creation, and study.",
      prompt: "Import this chapter, lecture video, and whiteboard image. Build one note I can ask questions from.",
      sources: ["PDF", "YouTube", "Image"],
      steps: [
        { title: "Add sources", body: "Upload or paste the materials you need for one topic." },
        { title: "Structure them", body: "YouWo extracts sections, timestamps, key terms, and citations." },
        { title: "Make it searchable", body: "Everything becomes one note that the agent can reason over." },
      ],
      outputs: ["Merged source note", "Clickable citations", "Topic sections"],
    },
    agent: {
      label: "Agent",
      title: "Approve the next step. Let the agent do it.",
      kicker: "Workflows that find tasks, gather context, and help you move faster.",
      body: "The agent looks for the task, brings in the context needed to complete it, and prepares each next step for your approval.",
      prompt: "Find the task I should work on, pull the right context, and prepare the next step for approval.",
      sources: ["Task", "Context", "Approval"],
      steps: [
        { title: "Find the task", body: "YouWo surfaces what needs attention before you search manually." },
        { title: "Grab the context", body: "It pulls the notes, files, recordings, and requirements needed to finish." },
        { title: "Ask for approval", body: "You approve the next step, then the agent does the work." },
      ],
      outputs: ["Next best task", "Context bundle", "Approved action"],
    },
    create: {
      label: "Create",
      title: "Create podcasts, videos, and writing with AI.",
      kicker: "Podcast ideas, video scripts, outlines, and writing help from your sources.",
      body: "Use AI to shape a podcast, video, or written piece while keeping yourself in the creative loop.",
      prompt: "Turn this lecture into a podcast outline, video script, and editable writing draft.",
      sources: ["Lecture", "Media brief", "Draft"],
      steps: [
        { title: "Choose a format", body: "Start a podcast, video script, outline, or writing draft." },
        { title: "Create with AI", body: "YouWo turns your sources into a structured first version." },
        { title: "Keep shaping it", body: "Edit, rewrite, expand, or turn it into another format." },
      ],
      outputs: ["Podcast outline", "Video script", "Writing draft"],
    },
    study: {
      label: "Study",
      title: "Study by asking through confusion.",
      kicker: "Explanations, translation, and answers grounded in your own material.",
      body: "When something is unclear, YouWo explains it, translates it, and answers follow-up questions from the source.",
      prompt: "Explain what I missed, translate the key idea, and answer my follow-up questions.",
      sources: ["Question", "Translation", "Source"],
      steps: [
        { title: "Ask the confusion", body: "Point to the exact part that does not make sense." },
        { title: "Explain and translate", body: "YouWo breaks it down and translates the key idea if needed." },
        { title: "Answer follow-ups", body: "Keep asking until the concept clicks." },
      ],
      outputs: ["Clear explanation", "Translated concept", "Answered question"],
    },
  },
};

const sourceIcon = (feature: FeatureKey, index: number) => {
  if (feature === "record") return index === 0 ? "lucide:audio-lines" : index === 1 ? "lucide:file-audio" : "lucide:languages";
  if (feature === "import") return index === 0 ? "lucide:file-text" : index === 1 ? "lucide:youtube" : "lucide:image";
  if (feature === "agent") return index === 0 ? "lucide:check-square" : index === 1 ? "lucide:folder-search" : "lucide:badge-check";
  if (feature === "create") return index === 0 ? "lucide:mic-2" : index === 1 ? "lucide:video" : "lucide:pen-line";
  return index === 0 ? "lucide:circle-help" : index === 1 ? "lucide:languages" : "lucide:notebook-text";
};

const YouWoConsumerWorkflowDemo: React.FC<Props> = ({ id }) => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState<FeatureKey>("record");
  const [activeStep, setActiveStep] = useState(0);

  const text = (key: string, defaultValue: string) => (
    t(`modern.workBrain.consumerDemo.${key}`, defaultValue) as string
  );

  const features: DemoFeature[] = featureOrder.map((key) => {
    const defaults = fallback.features[key];
    return {
      key,
      icon: featureIcons[key],
      label: text(`features.${key}.label`, defaults.label),
      title: text(`features.${key}.title`, defaults.title),
      body: text(`features.${key}.body`, defaults.body),
      sources: defaults.sources.map((value, index) => text(`features.${key}.sources.${index}`, value)),
      steps: defaults.steps.map((step, index) => ({
        title: text(`features.${key}.steps.${index}.title`, step.title),
        body: text(`features.${key}.steps.${index}.body`, step.body),
      })),
      outputs: defaults.outputs.map((value, index) => text(`features.${key}.outputs.${index}`, value)),
    };
  });

  const activeFeature = features.find((feature) => feature.key === activeKey) ?? features[0];
  const currentStep = activeFeature.steps[activeStep] ?? activeFeature.steps[0];

  useEffect(() => {
    setActiveStep(0);
  }, [activeKey]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveStep((step) => (step + 1) % activeFeature.steps.length);
    }, 2800);

    return () => window.clearInterval(interval);
  }, [activeFeature.steps.length]);

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    target.style.setProperty("--demo-x", `${event.clientX - rect.left}px`);
    target.style.setProperty("--demo-y", `${event.clientY - rect.top}px`);
  };

  return (
    <section className="yw-workflow-showcase" id={id}>
      <div className="yw-workflow-heading">
        <h2>{text("title", fallback.title)}</h2>
      </div>

      <div className="yw-workflow-shell" onPointerMove={handlePointerMove}>
        <div className="yw-workflow-rail" aria-label={text("featureLabel", fallback.featureLabel)}>
          <div className="yw-workflow-rail-heading">
            <span>{text("featureLabel", fallback.featureLabel)}</span>
            <strong>{text("hoverHint", fallback.hoverHint)}</strong>
          </div>
          {features.map((feature) => (
            <button
              aria-pressed={activeKey === feature.key}
              className={activeKey === feature.key ? "yw-workflow-choice yw-workflow-choice-active" : "yw-workflow-choice"}
              key={feature.key}
              onClick={() => setActiveKey(feature.key)}
              onFocus={() => setActiveKey(feature.key)}
              onMouseEnter={() => setActiveKey(feature.key)}
              type="button"
            >
              <Icon icon={feature.icon} />
              <span>
                <strong>{feature.label}</strong>
              </span>
              <Icon className="yw-workflow-arrow" icon="lucide:arrow-right" />
            </button>
          ))}
        </div>

        <div className="yw-workflow-stage" aria-live="polite">
          <div className="yw-workspace-window">
            <div className="yw-workspace-topbar">
              <div className="yw-window-controls" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
              <div className="yw-workspace-address">
                <Icon icon="lucide:sparkles" />
                <span>{text("workspaceTitle", fallback.workspaceTitle)}</span>
              </div>
              <strong>{activeFeature.label}</strong>
            </div>

            <div className="yw-workspace-grid">
              <aside className="yw-source-pane">
                <div className="yw-pane-title">
                  <span>{text("inputLabel", fallback.inputLabel)}</span>
                </div>

                <div className="yw-source-list">
                  {activeFeature.sources.map((source, index) => (
                    <button
                      className={index === activeStep ? "yw-source-item yw-source-item-active" : "yw-source-item"}
                      key={source}
                      onClick={() => setActiveStep(Math.min(index, activeFeature.steps.length - 1))}
                      type="button"
                    >
                      <Icon icon={sourceIcon(activeFeature.key, index)} />
                      <span>
                        <b>{source}</b>
                      </span>
                    </button>
                  ))}
                </div>
              </aside>

              <main className="yw-main-pane">
                <section className="yw-work-card">
                  <span className="yw-work-card-label">
                    <Icon icon={activeFeature.icon} />
                    {text("activeLabel", fallback.activeLabel)}
                  </span>
                  <h3>{activeFeature.title}</h3>
                  <p>{activeFeature.body}</p>
                </section>

                <div className="yw-approval-card">
                  <div className="yw-approval-icon">
                    <Icon icon="lucide:bot" />
                  </div>
                  <div>
                    <span>{text("approvalLabel", fallback.approvalLabel)}</span>
                    <strong>{currentStep.title}</strong>
                    <p>{currentStep.body}</p>
                  </div>
                  <button
                    onClick={() => setActiveStep((activeStep + 1) % activeFeature.steps.length)}
                    type="button"
                  >
                    {text("approveCta", fallback.approveCta)}
                  </button>
                </div>
              </main>

              <aside className="yw-output-pane">
                <div className="yw-output-card">
                  <span>{text("outputLabel", fallback.outputLabel)}</span>
                  {activeFeature.outputs.map((output) => (
                    <strong key={output}>
                      <Icon icon="lucide:check" />
                      {output}
                    </strong>
                  ))}
                </div>

                <a className="yw-workflow-cta" href={buildAppLink()}>
                  {text("primaryCta", fallback.primaryCta)}
                  <Icon icon="lucide:arrow-up-right" />
                </a>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default YouWoConsumerWorkflowDemo;
